import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Select, Input, Form, InputNumber, Typography } from "antd";
import styled from "styled-components";
import tokens from "../../theme/tokens";
import { buildCategoryOptions } from "./NegativeExampleModal";
import SubtypeSelect from "./SubtypeSelect";

const { TextArea } = Input;
const { Text } = Typography;

/**
 * Admin-side "Add gold standard" modal used from
 * Settings > AI Categorization. Headless re: API — the caller dispatches
 * `createGoldStandardFromDocument` with the payload this modal produces.
 *
 * v1 scope is "pick from a matter": admin enters a DMS document ID +
 * canonical category. We deliberately skip the "upload PDF" path because
 * BE PR #512 deferred the upload endpoint; once it lands, this modal can
 * grow a radio toggle without changing the caller contract.
 */

const Hint = styled.div`
  margin-top: 4px;
  color: ${tokens.text.tertiary};
  font-size: 12px;
  font-family: ${tokens.typography.fontFamily};
`;

const AddGoldStandardModal = ({
  open,
  categories = [],
  subtypes = [],
  saving = false,
  success = false,
  error = "",
  onCancel,
  onSubmit,
}) => {
  const options = useMemo(() => buildCategoryOptions(categories), [categories]);
  const [documentId, setDocumentId] = useState(null);
  const [category, setCategory] = useState("");
  const [subtype, setSubtype] = useState("");
  const [notes, setNotes] = useState("");
  const [touched, setTouched] = useState({ documentId: false, category: false });

  useEffect(() => {
    if (!open) return;
    setDocumentId(null);
    setCategory("");
    setSubtype("");
    setNotes("");
    setTouched({ documentId: false, category: false });
  }, [open]);

  const docIdValid = Number.isInteger(documentId) && documentId > 0;
  const categoryValid = Boolean(category);
  const canSubmit = docIdValid && categoryValid && !saving && !success;

  const handleSubmit = () => {
    if (!canSubmit) {
      setTouched({ documentId: true, category: true });
      return;
    }
    onSubmit({
      documentId,
      category,
      subtype: subtype || undefined,
      notes: notes.trim() ? notes.trim() : undefined,
    });
  };

  return (
    <Modal
      open={open}
      title="Add gold standard"
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={saving ? "Saving…" : success ? "Saved" : "Add to library"}
      cancelText="Cancel"
      okButtonProps={{
        disabled: !canSubmit,
        "data-testid": "add-gold-standard-submit",
      }}
      cancelButtonProps={{ disabled: saving }}
      maskClosable={!saving}
      destroyOnHidden
      data-testid="add-gold-standard-modal"
    >
      <Form layout="vertical">
        <Form.Item
          label="Document ID"
          required
          validateStatus={touched.documentId && !docIdValid ? "error" : ""}
          help={
            touched.documentId && !docIdValid
              ? "Enter a positive document ID (you can copy it from the document URL)."
              : null
          }
        >
          <InputNumber
            value={documentId}
            onChange={(value) => {
              setDocumentId(value);
              setTouched((t) => ({ ...t, documentId: true }));
            }}
            disabled={saving || success}
            placeholder="e.g. 1234"
            min={1}
            style={{ width: "100%" }}
            data-testid="add-gold-standard-doc-id"
          />
          <Hint>
            The document must already exist in DMS. We'll pull its filename
            and short summary from there.
          </Hint>
        </Form.Item>

        <Form.Item
          label="Category"
          required
          validateStatus={touched.category && !categoryValid ? "error" : ""}
          help={
            touched.category && !categoryValid
              ? "Pick the canonical category for this document."
              : null
          }
        >
          <Select
            placeholder="Select a category"
            value={category || undefined}
            onChange={(value) => {
              setCategory(value);
              setTouched((t) => ({ ...t, category: true }));
            }}
            disabled={saving || success}
            showSearch
            optionFilterProp="label"
            options={options}
            data-testid="add-gold-standard-category"
          />
        </Form.Item>

        <SubtypeSelect
          subtypes={subtypes}
          value={subtype}
          category={category}
          disabled={saving || success}
          onChange={setSubtype}
          testId="add-gold-standard-subtype"
        />

        <Form.Item
          label="Internal notes (optional)"
          tooltip="Visible to admins only. Use this to capture why this PDF is the canonical example."
        >
          <TextArea
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={saving || success}
            placeholder="Optional context for other admins."
            data-testid="add-gold-standard-notes"
          />
        </Form.Item>

        {error && (
          <Text type="danger" role="alert" data-testid="add-gold-standard-error">
            {error}
          </Text>
        )}
      </Form>
    </Modal>
  );
};

AddGoldStandardModal.propTypes = {
  open: PropTypes.bool.isRequired,
  categories: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape({
      results: PropTypes.array,
      data: PropTypes.array,
      categories: PropTypes.array,
    }),
  ]),
  subtypes: PropTypes.array,
  saving: PropTypes.bool,
  success: PropTypes.bool,
  error: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default AddGoldStandardModal;
